// A compact approval becomes a receipt only after the write completes.
import { Check, X } from 'lucide-react';
import { Fragment, type ReactNode, useId } from 'react';

import { Spinner } from '@apps/web/components/ui/spinner';
import { type ShiftWriteCard } from '@apps/web/features/phoebe_chat_v3/model/shift_write_card';
import { ResolvedReferenceText } from '@apps/web/features/phoebe_chat_v3/ui/primitives/reference_badge';

type WriteState =
    | Readonly<{
          status: 'pending';
          disabled?: boolean;
          // Separate from `disabled` so an unanswered question blocks
          // Approve without also blocking Deny.
          allowDisabled?: boolean;
          onAllow: () => void;
          onDeny: () => void;
      }>
    | Readonly<{
          status: 'running' | 'succeeded' | 'failed' | 'denied';
          resultSummary?: string;
      }>;

export function CompactWriteCard({
    card,
    state,
    notices = [],
    children,
}: Readonly<{
    card: ShiftWriteCard;
    state: WriteState;
    notices?: ReadonlyArray<string>;
    /** Anything the card must answer before it can be approved. */
    children?: ReactNode;
}>): ReactNode {
    const summaryId = useId();
    const pending = state.status === 'pending';
    const succeeded = state.status === 'succeeded' && card.saved !== false;
    const settling = state.status === 'running';
    // A reason is prose and needs its own row; a bare outcome rides inline.
    const refusalReason =
        state.status === 'failed' ? (state.resultSummary ?? null) : null;
    const badgeLabel =
        pending || succeeded || settling || refusalReason !== null
            ? null
            : state.status === 'denied'
              ? 'Denied'
              : 'Not saved';
    const extra = (
        <Fragment>
            {notices.length > 0 && (
                <ul className="text-muted-foreground text-v3-chat-meta mt-2 space-y-1">
                    {notices.map((notice, index) => (
                        <li key={index}>
                            <ResolvedReferenceText text={notice} />
                        </li>
                    ))}
                </ul>
            )}
        </Fragment>
    );
    return (
        <section
            className="bg-card text-v3-chat w-full min-w-0 rounded-[12px] border px-3 py-2.5 [overflow-wrap:anywhere]"
            aria-busy={state.status === 'running'}
            aria-labelledby={summaryId}
        >
            <div className="flex items-center gap-x-3">
                <div
                    className="m-0 min-w-0 flex-1"
                    role={pending ? undefined : 'status'}
                >
                    <p className="m-0" id={summaryId}>
                        <ResolvedReferenceText
                            text={
                                succeeded
                                    ? card.completed_summary
                                    : card.summary
                            }
                        />
                    </p>
                    {extra}
                </div>
                {!pending && refusalReason === null && (
                    <span className="text-muted-foreground text-v3-chat-meta inline-flex shrink-0 items-center gap-1.5 self-center">
                        {succeeded ? (
                            <Check aria-hidden={true} className="size-4" />
                        ) : settling ? (
                            <Spinner className="size-4" />
                        ) : (
                            <X aria-hidden={true} className="size-4" />
                        )}
                        {badgeLabel === null ? (
                            <span className="sr-only">
                                {succeeded ? 'Saved' : 'Saving…'}
                            </span>
                        ) : (
                            badgeLabel
                        )}
                    </span>
                )}
            </div>
            {refusalReason !== null && (
                <div className="text-muted-foreground text-v3-chat-meta mt-2.5 flex items-start gap-1.5 border-t pt-2">
                    <X aria-hidden={true} className="size-4 shrink-0" />
                    <span className="min-w-0">
                        <ResolvedReferenceText text={refusalReason} />
                    </span>
                </div>
            )}
            {state.status === 'pending' && children}
            {state.status === 'pending' && (
                <div className="mt-2 flex gap-2">
                    <button
                        type="button"
                        disabled={
                            state.disabled === true ||
                            state.allowDisabled === true
                        }
                        onClick={state.onAllow}
                        className="bg-foreground text-background focus-visible:ring-ring text-v3-chat min-h-8 rounded-[9px] px-3 font-semibold focus-visible:ring-2 disabled:opacity-40"
                    >
                        Approve
                    </button>
                    <button
                        type="button"
                        disabled={state.disabled}
                        onClick={state.onDeny}
                        className="text-muted-foreground focus-visible:ring-ring text-v3-chat min-h-8 rounded-[9px] border px-3 font-semibold focus-visible:ring-2 disabled:opacity-40"
                    >
                        Deny
                    </button>
                </div>
            )}
        </section>
    );
}
